import z from 'zod';

import { SerializedStudyData } from './study-data-class';

export const SalientPhaseSchema = z.object({
  id: z.enum(['BMORE0', 'BMORE30']),
  phaseLocation: z.number(),
});
export type SalientPhase = z.infer<typeof SalientPhaseSchema>;

export class State {
  private static stateColorTemplate: Record<string, string> = {
    FREEBREATHING: 'var(--md-secondary)',
    BREATHHOLD: 'var(--md-primary)',
    // Add more states and colors as needed
  };

  private color: string;
  private start: { id: string; state: string; phaseLocation: number };
  private end: { id: string; state: string; phaseLocation: number };
  private numImages: number;
  private cardiacCycles: { systoles: never[]; diastoles: never[] };
  private duration: string;
  private heartrate: string;
  private salientPhases: SalientPhase[] = [];

  constructor(stateName: string, numImages: number) {
    this.color = State.stateColorTemplate[stateName];
    this.numImages = numImages;
    this.start = {
      id: 'start',
      state: stateName,
      phaseLocation: 0,
    };
    this.end = {
      id: 'end',
      state: stateName,
      phaseLocation: numImages - 1,
    };
    this.cardiacCycles = {
      systoles: [],
      diastoles: [],
    };

    this.salientPhases = [
      { id: 'BMORE0', phaseLocation: 0 },
      { id: 'BMORE30', phaseLocation: numImages - 1 },
    ];

    // ToDo: Calculate from dicom and add here among with other required info
    this.duration = 'N/A';
    this.heartrate = 'N/A';
  }

  static fromSerialized(
    stateName: string,
    stateData: SerializedStudyData['seriesObject'][string]['slicesArray'][number]['sliceObject']['states'][string],
  ) {
    const state = new State(stateName, stateData.numImages);
    // Set properties
    state.color = stateData.color;
    state.start.phaseLocation = stateData.start;
    state.end.phaseLocation = stateData.end;
    state.cardiacCycles = stateData.cardiacCycles;
    state.duration = stateData.duration;
    state.heartrate = stateData.heartrate;
    state.salientPhases = stateData.salientPhases;
    return state;
  }

  getStart(): number {
    return this.start.phaseLocation;
  }

  getEnd(): number {
    return this.end.phaseLocation;
  }

  getNumImages(): number {
    return this.numImages;
  }

  getColor(): string {
    return this.color;
  }

  getSalientPhases(): SalientPhase[] {
    return this.salientPhases;
  }

  getSalientPhaseById(phaseId: string): SalientPhase | undefined {
    return this.salientPhases.find(phase => phase.id === phaseId);
  }

  getSalientPhaseLocation(phaseId: string): number | undefined {
    const phase = this.getSalientPhaseById(phaseId);
    return phase ? phase.phaseLocation : undefined;
  }

  setSalientPhaseLocation(phaseId: string, newLocation: number): void {
    const phase = this.getSalientPhaseById(phaseId);
    if (phase) {
      phase.phaseLocation = Math.min(
        Math.max(newLocation, 0),
        this.numImages - 1,
      );
    }
  }

  updateStart(newPhase: number | null): void {
    if (newPhase === null) return;

    this.start.phaseLocation = Math.min(
      Math.max(newPhase, 0),
      this.getEnd() - 1,
    );
  }

  updateEnd(newPhase: number | null): void {
    if (newPhase === null) return;

    this.end.phaseLocation = Math.max(
      Math.min(this.getNumImages() - 1, newPhase),
      this.getStart() + 1,
    );
  }

  updateStartEnd(newStart: number, newEnd: number): void {
    this.updateEnd(newEnd);
    this.updateStart(newStart);
  }

  getCardiacCycles() {
    return this.cardiacCycles;
  }

  getDuration(): string {
    return this.duration;
  }

  getHeartrate(): string {
    return this.heartrate;
  }
}
